import React from 'react';
import DetailModal from 'components/modals/DetailModal';
import { DetailCardType } from 'types/DetailPageTypes';
import { HeaderButtonsConfig } from 'types/ButtonTypes';
import ContactForm from '../../views/contacts/ContactForm';
import PersonForm from '../../views/contacts/PersonForm';
import LocationForm from '../../views/contacts/LocationForm';

// Function to determine the client field link url based on its entity type
const determineLinkUrl = (recipient: any) => {
    if (recipient && recipient['entity_type'] && recipient['id']) {
        const baseUrl = recipient['entity_type'] === 'organization' ? '/contacts/organization' : '/contacts/person';
        return `${baseUrl}/${recipient['id']}`;
    }
    return null;
}

// Rename the name of the function:
const ProposalModal: React.FC<{ viewKey?: string, itemId?: number }> = ({ viewKey, itemId }) => {

    // Specify the parameters of this detail modal:
    const apiObject = 'proposal';
    const object = 'proposal';
    const showStatusBar = false;

    // Define the header buttons of the modal:
    const headerButtons: HeaderButtonsConfig = {
        leftButtons: [
            // The pdf preview button is kept out of the header until the feature is released
            // {
            //     type: 'secondary',
            //     label: 'button.preview_pdf_button_label',
            //     action: 'preview_pdf',
            //     objectType: 'proposal',
            //     allowedRight: ['can_download'],
            //     query: {
            //         endpoint: 'get_pdf_layout_list/proposal',
            //         object: 'pdf_layout',
            //         defaultParams: { deleted: false },
            //     },
            // },
            {
                type: 'secondary',
                label: 'button.download_pdf_button_label',
                action: 'download_pdf',
                objectType: 'proposal',
                allowedRight: ['can_download'],
                query: {
                    endpoint: 'get_pdf_layout_list/proposal',
                    object: 'pdf_layout',
                    defaultParams: { deleted: false },
                },
            },
            {   
                type: 'dropdown',
                label: 'button.more_button_label',
                buttons: [
                    {
                        label: 'button.duplicate_proposal_button_label',
                        action: 'duplicate_document',
                        objectType: 'proposal',
                        restrictedRight: ['only_view'],
                    },
                    {
                        label: 'button.reopen_proposal_button_label',
                        action: 'reopen_proposal',
                        condition: [{ field: 'status', values: ['accepted', 'rejected'] }],
                        restrictedRight: ['only_view'],
                    },
                    {
                        label: 'button.delete_proposal_button_label',
                        action: 'delete',
                        restrictedRight: ['only_view'],
                        allowedRight: ['can_delete'],
                    },
                ]
            },
        ], 
        rightButtons: [
            {   
                type: 'primary',
                label: 'button.send_proposal_button_label',
                action: 'send_proposal',
                restrictedRight: ['only_view'],
                customClass: 'sent_proposal',
                condition: [{ field: 'status', values: ['draft'] }]
            },
            {   
                type: 'primary',
                label: 'button.rejected_proposal_button_label',
                action: 'proposal_rejected',
                restrictedRight: ['only_view'],
                customClass: 'rejected',
                condition: [{ field: 'status', values: ['sent', 'expired'] }]
            },
            {   
                type: 'primary',
                label: 'button.accepted_proposal_button_label',
                action: 'proposal_accepted',
                restrictedRight: ['only_view'],
                customClass: 'accepted',
                condition: [{ field: 'status', values: ['sent', 'expired'] }]
            },
            {
                type: 'secondary',
                label: 'button.create_order_button_label',
                action: 'create_order',
                restrictedRight: ['only_view'],
                allowedFeature: ['orders'],
                condition: [{ field: 'status', values: ['accepted'] }],
                query: {
                    endpoint: 'get_workflow_for_feature_list/order',
                    object: 'order_workflow',
                    defaultParams: { is_active: true },
                },
            },
        ]
    }

    // Define the summary of the modal
    const summary = true;

    // Define the base fields of the modal
    const fields: DetailCardType[] = [
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'proposal.general.recipient_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'recipient',
                            label: 'proposal.general.recipient_label',
                            placeholder: 'proposal.general.recipient_placeholder',
                            objectName: 'contact.general.object_name.singular',
                            query: {
                                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                                endpoint: 'get_contact_search',
                                params: {
                                    contact_type: ['contact', 'lead', 'client'] 
                                }
                            },
                            viewModeLinkUrl: { function: determineLinkUrl },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
                            },
                            // A contact created from a proposal starts as a lead
                            addNewItem: { method: 'modal-form', modalForm: <ContactForm />, linkedItem: { contact_type: 'lead' } }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'recipient_person',
                            label: 'proposal.general.recipient_person_label',
                            placeholder: 'proposal.general.recipient_person_placeholder',
                            objectName: 'person.general.object_name.singular',
                            query: { endpoint: 'get_person_search' },
                            dataCondition: [{'recipient.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/person' },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'address',
                            label: 'proposal.general.address_label',
                            placeholder: 'proposal.general.address_placeholder',
                            objectName: 'location.general.object_name.singular',
                            query: { endpoint: 'get_location_search' },
                            selectionFormat: 'address',
                            optionFormat: {
                                title: { field: 'address', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> }
                        },
                    ],
                },
                {
                    fields: [
                        
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'proposal.general.details_header',
                    fields: [
                        { 
                            type: 'dropdown', 
                            name: 'workflow', 
                            label: 'proposal.general.type_label', 
                            apiRequest: [{
                                endpoint: 'get_workflow_list',
                                object: 'workflow',
                                params: { 
                                    is_active: true, 
                                    connected_feature: 'proposal' 
                                },
                                useCache: true
                            }],
                            selectionFormat: 'name',
                            optionFormat: 'name',
                            orderOptions: true,
                            backendField: 'id',
                            disabled_selected: 'proposal.general.type_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'dropdown', 
                            name: 'division', 
                            label: 'job.general.division_label', 
                            apiRequest: [{
                                endpoint: 'get_division_list',
                                object: 'division',
                                params: { is_active: true },
                                useCache: true
                            }],
                            activeFeature: 'divisions',
                            disabled_selected: 'job.general.division_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'dropdown', 
                            name: 'managed_by_user', 
                            label: 'proposal.general.managed_by_user_label', 
                            apiRequest: [{
                                endpoint: 'get_user_list',
                                object: 'office_user',
                                params: { is_active: true, office_access: true },
                                useCache: true
                            }],
                            disabled_selected: 'proposal.general.managed_by_user_disabled_selected',
                            optionFormat: 'full_name',
                            selectionFormat: 'full_name',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                    ],
                },
                {
                    fields: [
                        { 
                            type: 'reference', 
                            name: 'reference',
                            label: 'proposal.general.reference_label',
                        },
                        {
                            type: 'sent-date', 
                            name: 'proposal_date', 
                            label: 'proposal.general.proposal_date_label',
                            tooltipText: 'proposal.general.proposal_date_tooltipText',
                            isEditable: { editability: false },
                        },
                        {
                            type: 'expiration-days',
                            name: 'expiration_days',
                            label: 'proposal.general.expiration_date_label',
                            featureName: 'proposal',
                        }
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'proposal.general.introduction_text_header',
            alignment: 'stacked',
            fields: [
                { 
                    type: 'textarea', 
                    name: 'introduction_text', 
                    emptyLabel: 'proposal.general.introduction_text_emptyLabel',
                    placeholder: 'proposal.general.introduction_text_placeholder', 
                    enableStyling: 'simple',
                    showToolbarInitially: true,
                    rows: 3,
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'proposal.general.lines_header',
            fields: [
                { 
                    name: 'lines',
                    type: 'document-lines',
                    featureName: 'proposal',
                    documentId: itemId,
                    apiRequest: [
                        {
                            endpoint: 'get_vatrate_list',
                            object: 'vatrate',
                            params: { is_active: true }
                        },
                        {
                            endpoint: 'get_ledgeraccount_list',
                            object: 'ledgeraccount',
                            params: { is_active: true, ledger_account_type: 'revenue' }
                        },
                        {
                            endpoint: 'get_currency_list',
                            object: 'currency',
                            params: {}
                        }
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'proposal.general.terms_and_conditions_header',
            alignment: 'stacked',
            fields: [
                {
                    type: 'condition-lines',
                    name: 'condition_lines',
                },
                { 
                    type: 'textarea', 
                    name: 'terms_and_conditions', 
                    emptyLabel: 'proposal.general.terms_and_conditions_emptyLabel',
                    placeholder: 'proposal.general.terms_and_conditions_placeholder', 
                    enableStyling: 'simple',
                    showToolbarInitially: true,
                    rows: 3,
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'proposal.general.attachments_header',
            fields: [
                {
                    type: 'attachments',
                    name: 'attachments',
                    apiRequest: [{ 
                        endpoint: 'get_attachmenttype_list', 
                        object: 'attachmenttype', 
                        params: { 'deleted': false }
                    }]
                }
            ]
        },
    ]

    return (
        <DetailModal 
            viewKey={viewKey}
            objectType={object}
            apiObject={apiObject}
            itemId={itemId}
            headerButtons={headerButtons}
            statusBar={showStatusBar}
            sections={fields}
            summary={summary}
        />
    );
};

export default ProposalModal;